"use client";
import * as React from "react";
import { cn } from "../lib/cn";

/**
 * Button — heritage-modern. Square-ish (8px), no shadow, calm hover.
 * Variants:
 *   primary   → rust. THE one action per screen. Use sparingly.
 *   secondary → outlined forest. The common neutral action.
 *   solid     → solid forest. For dark/branded contexts.
 *   ghost     → quiet text button.
 *
 *   <Button>Cancel</Button>
 *   <Button variant="primary">Submit report</Button>
 *   <Button variant="solid" leftIcon={<Plus size={16} />}>New</Button>
 */
type Variant = "primary" | "secondary" | "solid" | "ghost";
type Size = "sm" | "md" | "lg";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const VARIANTS: Record<Variant, string> = {
  primary:
    "bg-[var(--ri-rust)] text-white border border-transparent hover:bg-[var(--ri-rust-deep)]",
  secondary:
    "bg-white text-[var(--ri-forest-green)] border border-[rgba(45,74,42,.25)] hover:border-[var(--ri-forest-green)] hover:bg-[#FBFAF6]",
  solid:
    "bg-[var(--ri-forest-green)] text-[var(--ri-cream)] border border-transparent hover:bg-[#1f4520]",
  ghost:
    "bg-transparent text-[var(--ri-forest-green)] border border-transparent hover:bg-[rgba(45,74,42,.06)]",
};

const SIZES: Record<Size, string> = {
  sm: "h-8 px-3 text-[13px] gap-1.5",
  md: "h-10 px-4 text-[14px] gap-2",
  lg: "h-12 px-6 text-[15px] gap-2",
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = "secondary", size = "md", leftIcon, rightIcon, className, children, ...rest }, ref) => (
    <button
      ref={ref}
      className={cn(
        "inline-flex items-center justify-center font-semibold rounded-[var(--ri-radius)]",
        "transition-colors duration-150 ease-[var(--ri-ease)]",
        "disabled:opacity-50 disabled:pointer-events-none focus-visible:outline-none",
        "focus-visible:ring-2 focus-visible:ring-[var(--ri-forest-green)] focus-visible:ring-offset-2",
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {leftIcon}
      {children}
      {rightIcon}
    </button>
  ),
);
Button.displayName = "Button";
